import { useEffect, useRef, useState } from "react";
import baseline from "./install-baseline.json";

// Build-time baseline shape — written by scripts/fetch-install-baseline.mjs
interface Baseline {
  generatedAt: string;
  baseline: number;
  lastMonth: number;
  ratePerSecond: number;
}

// Runtime delta from /api/installs
interface InstallsApi {
  ok: boolean;
  fetchedAt: string;
  lastMonth: number;
  ratePerSecond: number;
}

const b = baseline as Baseline;

/**
 * useInstallTicker — the honest, animated install counter.
 *
 * Math:
 *   1. Start at build-time baseline + (now − generatedAt) × ratePerSecond.
 *      This is what we'd estimate the real number to be RIGHT NOW based
 *      on the last-month tick rate at build time.
 *   2. On mount, fetch /api/installs to true-up the rate (the build-time
 *      rate can drift between deploys).
 *   3. Animate upward at the (possibly trued-up) ratePerSecond using
 *      requestAnimationFrame.
 *
 * Respects prefers-reduced-motion: returns a static, formatted number
 * with no animation when set.
 */
export function useInstallTicker() {
  const [reduceMotion, setReduceMotion] = useState(false);

  // The displayed value. Initialized to the extrapolated current value
  // based on build-time baseline so the very first paint already has a
  // sensible number (no jump from 0).
  const computeInitial = () => {
    const ageSeconds = Math.max(0, (Date.now() - new Date(b.generatedAt).getTime()) / 1000);
    return b.baseline + ageSeconds * b.ratePerSecond;
  };
  const [value, setValue] = useState<number>(computeInitial);
  const rateRef = useRef<number>(b.ratePerSecond);
  const lastTickRef = useRef<number>(performance.now());
  const valueRef = useRef<number>(value);
  valueRef.current = value;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Runtime true-up: fetch /api/installs once on mount, replace rate.
  useEffect(() => {
    let cancelled = false;
    fetch("/api/installs", { headers: { accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: InstallsApi | null) => {
        if (cancelled || !json || !json.ok) return;
        if (typeof json.ratePerSecond === "number" && Number.isFinite(json.ratePerSecond)) {
          rateRef.current = json.ratePerSecond;
        }
      })
      .catch(() => {
        // Silent failure — counter keeps ticking at the baseline rate.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Animation loop. Skip entirely when reduce-motion is set — render
  // the extrapolated value as a static number.
  useEffect(() => {
    if (reduceMotion) return;
    let raf = 0;
    const tick = (now: number) => {
      const dt = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      const next = valueRef.current + dt * rateRef.current;
      // Only re-render when we cross an integer boundary — otherwise
      // we'd thrash React 60× per second for sub-integer changes.
      if (Math.floor(next) !== Math.floor(valueRef.current)) {
        setValue(next);
      } else {
        valueRef.current = next;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reduceMotion]);

  return {
    value: Math.floor(value),
    reduceMotion,
    generatedAt: b.generatedAt,
    lastMonth: b.lastMonth,
  };
}
